import { forwardRef, useState } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

// Canonical field styling — the single source for text-ish controls.
// (Previously duplicated as formBuilder/editorParts.tsx `inputClass`.)
export const inputClass =
  'focus-ring rounded-lg border border-border bg-white/70 text-foreground dark:bg-white/10 transition-colors';

// Validation-highlight pattern: red ring on the control; pair with an
// inline error message near the cause (never a toast).
const invalidClass = 'border-destructive ring-1 ring-destructive';

type FieldSize = 'sm' | 'md';

// Story 2-147qa — Input/Select are SINGLE-LINE controls, so (unlike
// Textarea below) they sit on the shared control-height scale
// (tokens.css `--control-h-sm`/`--control-h-md`, via `h-control-sm`/
// `h-control`) instead of deriving their height from padding + line-height.
// This is a REAL convergence, not a no-op like Button's own version of this
// change: at HEAD, Input/Select's `md` rendered ~28px (`px-2.5 py-1.5
// text-xs`) — the same height as Button's `sm`, four to eight px SHORTER
// than Button's own `md` (36px) sitting next to it in the same row/filter
// bar. `md` now matches Button/IconButton/SegmentedControl's `md` exactly;
// `sm` (28px) is unchanged from what it already rendered. `text-label`'s
// 11px (story-2-136 dense role, `tailwind.config.ts` story 2-137) is kept
// for `sm`'s font size — only the height mechanism changed, not the type
// scale.
const HEIGHT_CLASS: Record<FieldSize, string> = {
  sm: 'h-control-sm px-2 text-label',
  md: 'h-control px-2.5 text-xs',
};

// Textarea has no single "control height" to converge on (it is
// multi-line by definition — forcing `h-control`/`h-control-sm` here would
// clip it to one row) — kept on its own padding-derived sizing, unchanged
// from Input/Select's pre-2-147qa values.
const TEXTAREA_SIZE_CLASS: Record<FieldSize, string> = {
  sm: 'px-2 py-1 text-label',
  md: 'px-2.5 py-1.5 text-xs',
};

const spinner = <ArrowPathIcon className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  size?: FieldSize;
  /** Content rendered inside the field's left edge (icon, currency symbol, …). */
  adornmentStart?: ReactNode;
  /** Content rendered inside the field's right edge. */
  adornmentEnd?: ReactNode;
  /**
   * Story 2-147qa — same loading contract as `Button`/`IconButton`: disables
   * the field, sets `aria-busy`, and shows a spinner in the field's
   * `adornmentEnd` slot (replacing any caller-supplied `adornmentEnd` while
   * loading — the same "spinner takes over the icon slot" rule `Button`'s
   * own `loading` prop already applies to its icon).
   */
  loading?: boolean;
  /**
   * Story 2-147xd. Renders the SAME naked shape the `adornmentStart`/
   * `adornmentEnd` branch below already produces (`border-0 bg-transparent
   * p-0`, no `HEIGHT_CLASS` fixed-height scale) but WITHOUT requiring a
   * real adornment — for a caller that genuinely needs a borderless,
   * caller-sized text field (`TemplateEditorPage.tsx`'s own inline
   * `text-2xl font-bold` title field, too large for the `sm`/`md`
   * control-height scale every other `Input` consumer sits on) rather than
   * a boxed field with a transparent background painted over it. Closes a
   * real design-language/no-raw-control finding without forcing a visual
   * mismatch onto `Input`'s own default boxed contract.
   */
  bare?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, size = 'md', adornmentStart, adornmentEnd, loading = false, bare = false, className = '', disabled, ...rest },
  ref,
) {
  const effectiveAdornmentEnd = loading ? spinner : adornmentEnd;
  const hasAdornment = Boolean(adornmentStart || effectiveAdornmentEnd);
  const isDisabled = disabled || loading;
  const input = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      className={
        hasAdornment || bare
          ? `flex-1 border-0 bg-transparent p-0 text-inherit outline-none disabled:cursor-not-allowed ${className}`
          : `${inputClass} ${HEIGHT_CLASS[size]} ${invalid ? invalidClass : ''} disabled:cursor-not-allowed disabled:opacity-50 ${className}`
      }
      {...rest}
    />
  );

  if (!hasAdornment) return input;

  return (
    <span
      className={`${inputClass} ${HEIGHT_CLASS[size]} ${invalid ? invalidClass : ''} ${isDisabled ? 'opacity-50' : ''} flex items-center gap-1.5 focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background`}
    >
      {adornmentStart && (
        <span aria-hidden="true" className="flex shrink-0 items-center text-muted-foreground">
          {adornmentStart}
        </span>
      )}
      {input}
      {effectiveAdornmentEnd && (
        // Story 2-148 part 7 finding, fixed here: unlike `adornmentStart`
        // (both real call sites are decorative icons/prefix text —
        // MagnifyingGlassIcon, a "$" symbol), `adornmentEnd` has real
        // INTERACTIVE consumers today (`DateInput.tsx`'s clear button,
        // `FilterBar.tsx`'s `ClearKeywordButton`) — blanket `aria-hidden`
        // here hid a real, focusable, clickable button from the
        // accessibility tree while leaving it fully tabbable/clickable, a
        // WCAG 4.1.2 violation (axe's `aria-hidden-focus`, `serious`) caught
        // live by the new standing a11y check. Not `aria-hidden` — a caller
        // passing purely decorative end content (the gallery's "AUD" demo)
        // is unaffected either way, since inert text has no accessible-name
        // implications to begin with. The loading spinner itself IS purely
        // decorative (aria-busy above already carries the state), so it
        // keeps its own aria-hidden on the icon itself, set above.
        <span className="flex shrink-0 items-center text-muted-foreground">{effectiveAdornmentEnd}</span>
      )}
    </span>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  size?: FieldSize;
  /** Shows a live `n/max` counter below the field. Requires `maxLength`. */
  showCount?: boolean;
  /** Inline error text, shown only after the field has been blurred once. */
  errorMessage?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, size = 'md', showCount = false, errorMessage, maxLength, className = '', onBlur, value, id, ...rest },
  ref,
) {
  const [touched, setTouched] = useState(false);
  const errorId = errorMessage && id ? `${id}-error` : undefined;
  const count = typeof value === 'string' ? value.length : 0;

  return (
    <div>
      <textarea
        ref={ref}
        id={id}
        maxLength={maxLength}
        value={value}
        aria-invalid={invalid || undefined}
        aria-describedby={errorId}
        className={`${inputClass} ${TEXTAREA_SIZE_CLASS[size]} ${invalid ? invalidClass : ''} w-full ${className}`}
        onBlur={(e) => {
          setTouched(true);
          onBlur?.(e);
        }}
        {...rest}
      />
      <div className="mt-1 flex items-center justify-between gap-2">
        <div>
          {touched && invalid && errorMessage && (
            <p id={errorId} role="alert" className="text-xs text-destructive">
              {errorMessage}
            </p>
          )}
        </div>
        {showCount && typeof maxLength === 'number' && (
          <p
            className="shrink-0 text-label text-muted-foreground"
            aria-hidden="true"
          >
            {count}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
  size?: FieldSize;
  /** Rendered as a disabled, hidden first option when no value is selected. */
  placeholder?: string;
  /**
   * Story 2-147qa — disables the control and sets `aria-busy`, same as
   * `Input`'s `loading`. UNLIKE `Input`, no spinner is injected: a native
   * `<select>` renders its own OS-drawn dropdown chevron inside its box and
   * has no child-content slot a decorative icon could sit in without
   * fighting that chrome (wrapping it in Input's adornment span would visibly
   * offset the native arrow in some browsers) — `disabled` + `aria-busy` is
   * the honest, cross-browser-safe version of this control's loading state.
   * A caller that needs a VISIBLE loading affordance next to a Select
   * renders its own spinner/label beside it (see the gallery demo).
   */
  loading?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, size = 'md', placeholder, loading = false, className = '', children, defaultValue, value, disabled, ...rest },
  ref,
) {
  // A controlled select (caller passes `value`) must never also receive a
  // non-undefined `defaultValue` — React warns and behavior is undefined.
  // The placeholder's '' fallback is an uncontrolled-only convenience.
  const resolvedDefaultValue = value === undefined ? (defaultValue ?? (placeholder ? '' : undefined)) : defaultValue;

  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      defaultValue={resolvedDefaultValue}
      value={value}
      className={`${inputClass} ${HEIGHT_CLASS[size]} ${invalid ? invalidClass : ''} disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...rest}
    >
      {placeholder && (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      )}
      {children}
    </select>
  );
});

// RangeInput — story 2-147xd. A genuine, named DS-primitive gap, not a
// silently-dropped violation: `FormPreview.tsx`'s own rating/slider field
// (story 2-147wg) already documented that `Input`'s own box styling
// (border/background/padding — built for TEXT-ish controls, per this
// file's own header comment) has no sane way to compose with a native
// OS-drawn range-slider thumb/track, so it stayed a raw `<input
// type="range">` rather than force a bad fit. This is the honest
// resolution instead: the exact same one native tag, moved into THIS
// file (the layer that legitimately renders native controls directly —
// the same reasoning `Input`/`Textarea`/`Select` above already carry) as
// its own thin, real primitive, closing the design-language/no-raw-control
// finding without inventing a heavier abstraction a single real consumer
// does not need.
export interface RangeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  className?: string;
}

export const RangeInput = forwardRef<HTMLInputElement, RangeInputProps>(function RangeInput(
  { className = '', ...rest },
  ref,
) {
  return <input ref={ref} type="range" className={`accent-primary ${className}`} {...rest} />;
});

// ColorInput — story 2-95b (G-22 tail sweep). The SAME finding RangeInput's
// own header comment names, for a color swatch instead of a slider: a raw
// `<input type="color">` has no sane way to compose with Input's boxed
// text-field chrome (border/background/padding), and the browser's own oval
// color-input chrome is ugly enough that BOTH real consumers
// (`formBuilder/editorParts.tsx`'s response-color swatch,
// `formBuilder/ReportDesigner.tsx`'s report accent-color swatch) had already
// independently hand-rolled the identical fix: a circular `<label>` showing
// the current value as its own `backgroundColor`, with the real
// `<input type="color">` absolutely positioned on top at `opacity-0` — the
// invisible input still opens the native OS color picker on click/keyboard
// (a real, focusable, operable control), the label is only what paints. This
// primitive is that composed shape, not the bare input alone, because the
// swatch-label IS the whole visible affordance at both real call sites —
// wrapping just the `<input>` and leaving the swatch as caller-hand-rolled
// markup would still leave the JSXOpeningElement duplicated at both sites.
type ColorInputSize = 'sm' | 'md';

const SWATCH_SIZE_CLASS: Record<ColorInputSize, string> = {
  sm: 'h-6 w-6', // editorParts.tsx's response-color swatch
  md: 'h-7 w-7', // ReportDesigner.tsx's accent-color swatch
};

export interface ColorInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'value'> {
  /** Hex (or any CSS color) value — painted onto the visible swatch AND passed to the real input. */
  value: string;
  size?: ColorInputSize;
  /** Accessible name for the invisible `<input>` — there is no visible text label, only the swatch. */
  'aria-label': string;
  /** Tooltip shown on the visible swatch (native `title`, not a second accessible-name mechanism). */
  title?: string;
}

export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(function ColorInput(
  { value, size = 'md', className = '', title, ...rest },
  ref,
) {
  return (
    <label
      title={title}
      className={`relative inline-block ${SWATCH_SIZE_CLASS[size]} shrink-0 cursor-pointer rounded-full border border-white/30 shadow-inner ${className}`}
      style={{ backgroundColor: value }}
    >
      <input
        ref={ref}
        type="color"
        value={value}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        {...rest}
      />
    </label>
  );
});
